import * as React from "react";
import { createPortal } from "react-dom";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  Stack,
  Tooltip,
  IconButton,
  Button,
  Chip,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import NoRows from "../../../components/DataGrid/animations/NoRows";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import httpClient from "../../../utils/httpClient";
import { useNavigate } from "react-router-dom";
import moment, { Moment } from "moment";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import {
  applyFilters,
  buildFilterSummary,
  formatUSD,
  SPEC_PRINT_PORTAL_ID,
  PrintColumn,
  FilterItem,
  Filters,
} from "../../../utils/listExport";
import {
  getSpecFilters,
  setSpecFilters,
  getSpecPagination,
  setSpecPagination,
  ListFilters,
} from "../../../utils/localStorage";
import { ListPageToolbar } from "../../../components/ListPageToolbar/ListPageToolbar";

// ── Types ────────────────────────────────────────────────────────────────────

type RowType = {
  analysisHistoryCount: number;
  buyer: string;
  buyerCombined: string;
  jobAddress: string;
  jobId: number;
  jobNumber: string;
  listPrice: number;
  planDescription: string;
  planElevationDescription: string;
  planElevationNumber: string;
  planNumber: string;
  profitAnalysisId: number;
  profitAnalysisCreatedOn?: Moment | string | null;
  paModifiedOn?: Moment | string | null;
  jobType: string;
  projectId: number;
  projectNumber: string;
  soldDate: string;
  startDate: string;
  subdivision: string;
  stageCode: string;
  stageDate: string;
  role: string;
  closeDate?: string;
  exitStrategy?: number | null;
  bpPercent?: number | null;
  blPercent?: number | null;
  bpPrice?: number | null;
  blPrice?: number | null;
  salesPriceAtBP?: number | null;
  salesPriceAtBL?: number | null;
  projectedCloseDate?: string | null;
};

// ── Legend definition ────────────────────────────────────────────────────────

const LEGEND_ITEMS = [
  { abbr: "Address", full: "Create / Revise / View profit analysis | Job street address" },
  { abbr: "Subdivision", full: "Community / subdivision name" },
  { abbr: "Job #", full: "Project-Job number" },
  { abbr: "Projected Close Date", full: "Projected close date for the job" },
  { abbr: "PA Updated On", full: "Most recent date of profit analysis creation or modification" },
  { abbr: "S [# days]", full: "Stage Code [Days since entering Stage 10]" },
  { abbr: "PL-EL", full: "Plan – Elevation description" },
  { abbr: "Web/List Price", full: "Current list price (USD)" },
  { abbr: "Exit %", full: "Exit strategy percentage" },
  { abbr: "BP %", full: "Business Plan percentage" },
  { abbr: "BP Sales Price", full: "Sales price at business plan (USD)" },
  { abbr: "BL %", full: "Bottom Line percentage" },
  { abbr: "BL Sales Price", full: "Sales price at bottom line (USD)" },
];

// ── Default filter/pagination state ─────────────────────────────────────────

const DEFAULT_STAGE = "All";
const DEFAULT_SEARCH = "";
const DEFAULT_COMMUNITIES: string[] = [];
const DEFAULT_PAGINATION = { page: 0, pageSize: 100 };
const EMPTY_FILTERS: Filters = { items: [], quickFilterValues: [] };

// ── Helpers ──────────────────────────────────────────────────────────────────

const toCleanString = (v: unknown): string | null => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s.length ? s : null;
};

const sanitizeFilterItems = (items: FilterItem[]): FilterItem[] =>
  (items ?? [])
    .map((item) => {
      if (!item) return null;
      const raw = item.value as unknown;

      if (Array.isArray(raw)) {
        const cleaned = raw.map(toCleanString).filter((v): v is string => Boolean(v));
        if (cleaned.length === 0) return null;
        return { ...item, operator: "isAnyOf", value: cleaned };
      }

      const cleaned = toCleanString(raw);
      if (!cleaned) return null;
      return { ...item, value: cleaned };
    })
    .filter((x): x is FilterItem => Boolean(x));

const sanitizeQuickFilter = (values: unknown[]): string[] =>
  (values ?? []).map(toCleanString).filter((v): v is string => Boolean(v));

const buildDataGridFilter = (listFilters: ListFilters): Filters => ({
  items: sanitizeFilterItems(listFilters.items ?? []),
  quickFilterValues: sanitizeQuickFilter(
    (listFilters.quickFilterValues ?? []) as unknown[]
  ),
});

// ── Print columns ─────────────────────────────────────────────────────────────

const PRINT_COLUMNS: PrintColumn<RowType>[] = [
  { field: "jobAddress", label: "Address", getValue: (r) => r.jobAddress ?? "" },
  { field: "subdivision", label: "Subdivision", getValue: (r) => r.subdivision ?? "" },
  {
    field: "jobNumber", label: "Job #",
    getValue: (r) => (r.projectNumber && r.jobNumber ? `${r.projectNumber}-${r.jobNumber}` : ""),
  },
  {
    field: "projectedCloseDate", label: "Projected Close Date",
    getValue: (r) => (!r.projectedCloseDate || r.projectedCloseDate === "0001-01-01T00:00:00" ? "" : moment(r.projectedCloseDate).format("L")),
  },
  {
    field: "paModifiedOn", label: "PA Updated On",
    getValue: (r) => {
      const v = r.paModifiedOn && r.paModifiedOn !== "0001-01-01T00:00:00"
        ? r.paModifiedOn
        : r.profitAnalysisCreatedOn;
      if (!v || v === "0001-01-01T00:00:00" || r.profitAnalysisId === 0) return "";
      return moment(v).format("L");
    },
  },
  { field: "stageCode", label: "Stage [# days]", getValue: (r) => r.stageCode + (r.stageDate && r.stageCode === "10" ? ` [${Math.floor((Date.now() - new Date(r.stageDate).getTime()) / 86_400_000)}d]` : "") },
  {
    field: "planElevation", label: "PL-EL",
    getValue: (r) => {
      const plan = r.planDescription ?? "";
      const elev = r.planElevationNumber ?? "";
      if (plan && elev) return `${plan} – ${elev}`;
      return plan || elev || "";
    },
  },
  { field: "listPrice", label: "Web/List Price", getValue: (r) => formatUSD(r.listPrice) },
  { field: "exitStrategy", label: "Exit %", getValue: (r) => (r.exitStrategy != null ? `${r.exitStrategy}%` : "") },
  { field: "bpPercent", label: "BP %", getValue: (r) => (r.bpPercent != null ? `${r.bpPercent}%` : "") },
  { field: "salesPriceAtBP", label: "BP Sales Price", getValue: (r) => formatUSD(r.salesPriceAtBP) },
  { field: "blPercent", label: "BL %", getValue: (r) => (r.blPercent != null ? `${r.blPercent}%` : "") },
  { field: "salesPriceAtBL", label: "BL Sales Price", getValue: (r) => formatUSD(r.salesPriceAtBL) },
];

// ── Mobile card ───────────────────────────────────────────────────────────────

function SpecMobileCard({ row, onNavigate }: { row: RowType; onNavigate: (path: string, state?: any) => void }) {
  const isCreateCase =
    row.profitAnalysisId === 0 && row.role === "Write" && (row.closeDate ?? null) === null;
  const isViewCase =
    (row.profitAnalysisId !== 0 && row.role === "Read") ||
    (row.profitAnalysisId !== 0 && row.role === "Write" &&
      (row.closeDate ?? null) !== null &&
      new Date(row.closeDate!) > new Date(new Date().setDate(new Date().getDate() - 60)));
  const isReviseCase =
    row.profitAnalysisId !== 0 && (row.closeDate ?? null) === null && row.role === "Write";

  const planElev = (() => {
    const plan = row.planDescription ?? "";
    const elev = row.planElevationNumber ?? "";
    if (plan && elev) return `${plan} – ${elev}`;
    return plan || elev || null;
  })();

  const jobNum = row.projectNumber && row.jobNumber
    ? `${row.projectNumber}-${row.jobNumber}` : null;

  const paDate = (() => {
    const v = row.paModifiedOn && row.paModifiedOn !== "0001-01-01T00:00:00"
      ? row.paModifiedOn : row.profitAnalysisCreatedOn;
    if (!v || v === "0001-01-01T00:00:00" || row.profitAnalysisId === 0) return null;
    return moment(v).format("L");
  })();

  const stageDays =
    row.stageDate && row.stageCode === "10"
      ? Math.floor((Date.now() - new Date(row.stageDate).getTime()) / 86_400_000)
      : null;

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1.5,
        borderRadius: 2,
        border: "1px solid #e5e7eb",
        "&:hover": { borderColor: "#9ca3af", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" },
      }}
    >
      <CardContent sx={{ pb: "12px !important", pt: 1.5, px: 2 }}>
        {/* Address + action buttons */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#111827", lineHeight: 1.4, pr: 1 }}>
            {row.jobAddress}
          </Typography>
          <Stack direction="row" spacing={0} sx={{ flexShrink: 0 }}>
            {isViewCase && (
              <Tooltip title="View PA">
                <IconButton size="small" href={`/spec/view/${row.profitAnalysisId}`}>
                  <VisibilityIcon fontSize="small" color="primary" />
                </IconButton>
              </Tooltip>
            )}
            {isReviseCase && (
              <Tooltip title="Revise PA">
                <IconButton size="small" href={`/spec/edit/${row.profitAnalysisId}`}>
                  <EditIcon fontSize="small" color="primary" />
                </IconButton>
              </Tooltip>
            )}
            {isCreateCase && (
              <Tooltip title="Create PA">
                <IconButton size="small" color="primary" onClick={() => onNavigate("/spec/create", { ...row, method: "fromHist" })}>
                  <AddCircleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>

        {/* Subdivision · Job # */}
        <Typography variant="caption" sx={{ color: "#6b7280", display: "block", mb: 0.5 }}>
          {row.subdivision}{jobNum ? ` · ${jobNum}` : ""}
        </Typography>

        {/* Stage chip + Plan-Elevation chip */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 0.75 }}>
          {row.stageCode && (
            <Chip
              label={`Stage ${row.stageCode}${stageDays !== null ? ` [${stageDays}d]` : ""}`}
              size="small"
              sx={{ fontSize: "0.7rem", height: 20, backgroundColor: "#f3f4f6", color: "#374151" }}
            />
          )}
          {planElev && (
            <Chip
              label={planElev}
              size="small"
              sx={{ fontSize: "0.7rem", height: 20, backgroundColor: "#f3f4f6", color: "#374151" }}
            />
          )}
        </Box>

        {/* Numeric / date fields */}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {row.listPrice ? (
            <Box>
              <Typography variant="caption" sx={{ color: "#9ca3af", display: "block", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: 0.5 }}>Web/List Price</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#111827", fontSize: "0.8rem" }}>{formatUSD(row.listPrice)}</Typography>
            </Box>
          ) : null}
          {row.exitStrategy != null && (
            <Box>
              <Typography variant="caption" sx={{ color: "#9ca3af", display: "block", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: 0.5 }}>Exit %</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#111827", fontSize: "0.8rem" }}>{row.exitStrategy}%</Typography>
            </Box>
          )}
          {row.bpPercent != null && (
            <Box>
              <Typography variant="caption" sx={{ color: "#9ca3af", display: "block", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: 0.5 }}>BP %</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#111827", fontSize: "0.8rem" }}>{row.bpPercent}%</Typography>
            </Box>
          )}
          {row.salesPriceAtBP != null && (
            <Box>
              <Typography variant="caption" sx={{ color: "#9ca3af", display: "block", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: 0.5 }}>Sales Price at BP</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#111827", fontSize: "0.8rem" }}>{formatUSD(row.salesPriceAtBP)}</Typography>
            </Box>
          )}
          {row.blPercent != null && (
            <Box>
              <Typography variant="caption" sx={{ color: "#9ca3af", display: "block", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: 0.5 }}>BL %</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#111827", fontSize: "0.8rem" }}>{row.blPercent}%</Typography>
            </Box>
          )}
          {row.salesPriceAtBL != null && (
            <Box>
              <Typography variant="caption" sx={{ color: "#9ca3af", display: "block", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: 0.5 }}>Sales Price at BL</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#111827", fontSize: "0.8rem" }}>{formatUSD(row.salesPriceAtBL)}</Typography>
            </Box>
          )}
          {row.projectedCloseDate && row.projectedCloseDate !== "0001-01-01T00:00:00" && (
            <Box>
              <Typography variant="caption" sx={{ color: "#9ca3af", display: "block", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: 0.5 }}>Proj. Close</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#111827", fontSize: "0.8rem" }}>{moment(row.projectedCloseDate).format("L")}</Typography>
            </Box>
          )}
          {paDate && (
            <Box>
              <Typography variant="caption" sx={{ color: "#9ca3af", display: "block", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: 0.5 }}>PA Updated</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#111827", fontSize: "0.8rem" }}>{paDate}</Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function SpecListPage() {
  const navigation = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Initialise state from localStorage
  const savedFilters = getSpecFilters();
  const savedPagination = getSpecPagination();

  const [rows, setRows] = React.useState<RowType[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);

  const [filt, setFilt] = React.useState<Filters>(() =>
    buildDataGridFilter(savedFilters)
  );

  const [stage, setStage] = React.useState<string>(savedFilters.stage ?? DEFAULT_STAGE);
  const [searchValue, setSearchValue] = React.useState<string>(
    (savedFilters.quickFilterValues?.[0] ?? DEFAULT_SEARCH) as string
  );
  const [selectedCommunities, setSelectedCommunities] = React.useState<string[]>(() => {
    const communityItem = savedFilters.items.find((i) => i.field === "subdivision");
    return Array.isArray(communityItem?.value) ? (communityItem!.value as string[]) : [];
  });
  const [communitySearch, setCommunitySearch] = React.useState<string>("");
  const [paginationModel, setPaginationModel] = React.useState(savedPagination);

  // ── Derived subdivision list ────────────────────────────────────────────

  const subdivisions = React.useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.subdivision).filter(Boolean))).sort(),
    [rows]
  );

  const filteredSubdivisions = React.useMemo(() => {
    if (!communitySearch.trim()) return subdivisions;
    const lower = communitySearch.toLowerCase();
    return subdivisions.filter((s) => s.toLowerCase().includes(lower));
  }, [subdivisions, communitySearch]);

  // ── Derived "has active filters" flag ───────────────────────────────────

  const hasActiveFilters = React.useMemo(
    () =>
      stage !== DEFAULT_STAGE ||
      searchValue !== DEFAULT_SEARCH ||
      selectedCommunities.length > 0 ||
      filt.items.length > 0 ||
      filt.quickFilterValues.length > 0,
    [stage, searchValue, selectedCommunities, filt]
  );

  // ── Persist helpers ─────────────────────────────────────────────────────

  const persistFilters = React.useCallback(
    (updatedItems: FilterItem[], updatedQuick: string[], updatedStage: string) => {
      setSpecFilters({ items: updatedItems, quickFilterValues: updatedQuick, stage: updatedStage });
    },
    []
  );

  const handlePaginationChange = (model: { page: number; pageSize: number }) => {
    setPaginationModel(model);
    setSpecPagination(model);
  };

  // ── Shared filter item builder ──────────────────────────────────────────

  const rebuildItems = (
    currentItems: FilterItem[],
    field: string,
    value: string | string[] | "All" | null
  ): FilterItem[] => {
    const otherItems = (currentItems ?? []).filter((item) => item.field !== field);

    if (value === "All" || value === null || value === undefined) return otherItems;

    if (Array.isArray(value)) {
      const cleaned = value.map(toCleanString).filter((v): v is string => Boolean(v));
      if (cleaned.length === 0) return otherItems;
      return [...otherItems, { id: `${field}-multi`, field, operator: "isAnyOf", value: cleaned }];
    }

    const cleaned = toCleanString(value);
    if (!cleaned) return otherItems;
    return [...otherItems, { id: `${field}-${cleaned}`, field, operator: "equals", value: cleaned }];
  };

  // ── Toolbar handlers ────────────────────────────────────────────────────

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setSearchValue(newValue);
    const newQuick = sanitizeQuickFilter([newValue]);
    setFilt((prev) => {
      const updated: Filters = { ...prev, quickFilterValues: newQuick };
      persistFilters(updated.items, newQuick, stage);
      return updated;
    });
  };

  const handleStageChange = (event: SelectChangeEvent<string>) => {
    const newValue = event.target.value;
    setStage(newValue);
    setFilt((prev) => {
      const newItems = rebuildItems(prev.items, "stageCode", newValue);
      const updated: Filters = { ...prev, items: newItems };
      persistFilters(newItems, sanitizeQuickFilter(prev.quickFilterValues), newValue);
      return updated;
    });
  };

  const handleCommunityChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as string[];
    setSelectedCommunities(value);
    setFilt((prev) => {
      const newItems = sanitizeFilterItems(rebuildItems(prev.items, "subdivision", value));
      const safeQuick = sanitizeQuickFilter(prev.quickFilterValues);
      persistFilters(newItems, safeQuick, stage);
      return { ...prev, items: newItems, quickFilterValues: safeQuick };
    });
  };

  const handleCommunityDelete = (val: string) => {
    const next = selectedCommunities.filter((c) => c !== val);
    setSelectedCommunities(next);
    setFilt((prev) => {
      const newItems = sanitizeFilterItems(rebuildItems(prev.items, "subdivision", next));
      const safeQuick = sanitizeQuickFilter(prev.quickFilterValues);
      persistFilters(newItems, safeQuick, stage);
      return { ...prev, items: newItems, quickFilterValues: safeQuick };
    });
  };

  const handleFilterModelChange = (newFilterModel: Filters) => {
    const safeItems = sanitizeFilterItems(newFilterModel.items ?? []);
    const safeQuick = sanitizeQuickFilter(newFilterModel.quickFilterValues ?? []);
    const safe: Filters = { ...newFilterModel, items: safeItems, quickFilterValues: safeQuick };
    setFilt(safe);
    persistFilters(safeItems, safeQuick, stage);
  };

  // ── Clear all filters ───────────────────────────────────────────────────

  const handleClearFilters = () => {
    setStage(DEFAULT_STAGE);
    setSearchValue(DEFAULT_SEARCH);
    setSelectedCommunities(DEFAULT_COMMUNITIES);
    setCommunitySearch("");
    setFilt(EMPTY_FILTERS);
    persistFilters([], [], DEFAULT_STAGE);
  };

  // ── Data loading ────────────────────────────────────────────────────────

  const loadJobs = () => {
    setLoading(true);
    httpClient
      .get(`/api/jobs/`)
      .then((res) => {
        const uniqueIds: Record<number, boolean> = {};
        const dataArray = Array.isArray(res?.data) ? res.data : [];
        const uniqueObjects = dataArray.filter((obj: any) => {
          if (!uniqueIds[obj.jobId]) {
            uniqueIds[obj.jobId] = true;
            return true;
          }
          return false;
        });
        setRows(uniqueObjects as any);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.error(err);
      });
  };

  React.useEffect(() => {
    loadJobs();
  }, []);

  // ── Column definitions ──────────────────────────────────────────────────

  const columns: GridColDef[] = [
    {
      field: "jobAddress",
      headerName: "Address",
      minWidth: 240,
      flex: 2,
      filterable: true,
      sortComparator: (v1: string, v2: string) => {
        const parse = (addr: string) => {
          const m = addr?.match(/^(\d+)\s+(.*)/);
          return m ? [m[2].toLowerCase(), Number(m[1])] : [addr?.toLowerCase() ?? "", 0];
        };
        const [name1, num1] = parse(v1);
        const [name2, num2] = parse(v2);
        if (name1 < name2) return -1;
        if (name1 > name2) return 1;
        return (num1 as number) - (num2 as number);
      },
      renderCell: (params: any) => {
        const isCreateCase =
          params.row.profitAnalysisId === 0 &&
          params.row.role === "Write" &&
          (params?.row?.closeDate ?? null) === null;
        const isViewCase =
          (params.row.profitAnalysisId !== 0 && params.row.role === "Read") ||
          (params.row.profitAnalysisId !== 0 &&
            params.row.role === "Write" &&
            (params?.row?.closeDate ?? null) !== null &&
            new Date(params.row.closeDate) >
            new Date(new Date().setDate(new Date().getDate() - 60)));
        const isReviseCase =
          params.row.profitAnalysisId !== 0 &&
          (params?.row?.closeDate ?? null) === null &&
          params.row.role === "Write";

        const href = isReviseCase
          ? `/spec/edit/${params.row.profitAnalysisId}`
          : isViewCase
            ? `/spec/view/${params.row.profitAnalysisId}`
            : "#";

        const tooltipTitle = isCreateCase
          ? "Create PA"
          : isReviseCase
            ? "Revise PA"
            : isViewCase
              ? "View PA"
              : "";

        return (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: "100%", overflow: "hidden" }}>
            {/* Action icon */}
            <Box sx={{ flexShrink: 0 }}>
              {isViewCase && (
                <Tooltip title={`View PA for ${params.row.jobAddress}`} placement="left">
                  <IconButton size="small" href={`/spec/view/${params.row.profitAnalysisId}`}>
                    <VisibilityIcon fontSize="small" color="primary" />
                  </IconButton>
                </Tooltip>
              )}
              {isReviseCase && (
                <Tooltip title={`Revise PA for ${params.row.jobAddress}`} placement="left">
                  <IconButton size="small" href={`/spec/edit/${params.row.profitAnalysisId}`}>
                    <EditIcon fontSize="small" color="primary" />
                  </IconButton>
                </Tooltip>
              )}
              {isCreateCase && (
                <Tooltip title={`Create PA for ${params.row.jobAddress}`} placement="left">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => navigation("/spec/create", { state: { ...params.row, method: "fromHist" } })}
                  >
                    <AddCircleIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {!isViewCase && !isReviseCase && !isCreateCase && <Box sx={{ width: 34 }} />}
            </Box>

            {/* Address link with ellipsis */}
            <Tooltip title={params.value ?? ""} placement="top">
              <Link
                color="inherit"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  cursor: (isCreateCase || isReviseCase || isViewCase) ? "pointer" : "default",
                  textDecoration: (isCreateCase || isReviseCase || isViewCase) ? "underline" : "none",
                }}
                href={href}
                onClick={(e) => {
                  if (isCreateCase) {
                    e.preventDefault();
                    navigation("/spec/create", { state: { ...params.row, method: "fromHist" } });
                  } else if (!isViewCase && !isReviseCase) {
                    e.preventDefault();
                  }
                }}
              >
                {params.value}
              </Link>
            </Tooltip>
          </Stack>
        );
      },
    },
    {
      field: "subdivision",
      headerName: "Subdivision",
      minWidth: 160,
      flex: 1.5,
      filterable: true,
      renderCell: (params) => (
        <Tooltip title={params.value ?? ""} placement="top">
          <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "100%" }}>
            {params.value}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "jobNumber",
      headerName: "Job #",
      headerAlign: "left",
      align: "left",
      minWidth: 120,
      flex: 0.8,
      filterable: true,
      valueGetter: (_params: any, row: any) => {
        if (!row?.projectNumber || !row?.jobNumber) return null;
        return `${row?.projectNumber}-${row?.jobNumber}`;
      },
      renderCell: (params) => <div>{params.value}</div>,
    },
    {
      field: "projectedCloseDate",
      headerName: "Projected Close Date",
      filterable: true,
      minWidth: 140,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueFormatter: (value: any) => {
        if (!value || value === "0001-01-01T00:00:00") return "";
        return moment(value).format("L");
      },
      renderCell: (params) => {
        const val = params.row.projectedCloseDate;
        if (!val || val === "0001-01-01T00:00:00") return <div />;
        return <div>{moment(val).format("L")}</div>;
      },
    },
    {
      field: "paModifiedOn",
      headerName: "PA Updated On",
      minWidth: 120,
      flex: 1,
      filterable: true,
      align: "center",
      headerAlign: "center",
      valueGetter: (_params: any, row: any) => {
        const v =
          row.paModifiedOn && row.paModifiedOn !== "0001-01-01T00:00:00"
            ? row.paModifiedOn
            : row.profitAnalysisCreatedOn;
        if (!v || v === "0001-01-01T00:00:00" || row.profitAnalysisId === 0) return null;
        return new Date(v).getTime();
      },
      renderCell: (params) => (
        <div>{params.value ? moment(params.value).format("L") : ""}</div>
      ),
    },

    // ── Stage: "S [# days]" ────────────────────────────────────────────────────
    {
      field: "stageCode",
      headerName: "Stage",
      filterable: true,
      minWidth: 100,
      flex: 0.7,
      align: "center",
      headerAlign: "center",
      valueGetter: (_params: any, row: any) => {
        const code = row?.stageCode ? Number(row.stageCode) : 0;
        const days =
          row?.stageDate && row?.stageCode === "10"
            ? Math.floor((Date.now() - new Date(row.stageDate).getTime()) / 86_400_000)
            : 0;
        // Pack into a single number: stage * 100000 + days
        // This sorts by stage first, then by days within stage 10
        return code * 100_000 + days;
      },
      renderHeader: () => (
        <Tooltip title="Stage Code [Days since entering Stage 10]" placement="top">
          <Box
            sx={{
              fontWeight: 600,
              textTransform: "uppercase",
              fontSize: "0.875rem",
              color: "#374151",
              cursor: "default",
              userSelect: "none",
              whiteSpace: "nowrap",
            }}
          >
            S&nbsp;[# days]
          </Box>
        </Tooltip>
      ),
      renderCell: (params) => {
        const code = params.row.stageCode ?? "";
        const stageDate = params.row.stageDate;
        const daysSince =
          stageDate
            ? Math.floor(
              (Date.now() - new Date(stageDate).getTime()) / 86_400_000
            )
            : null;

        return (
          <Tooltip
            title={
              (daysSince !== null && daysSince > 0 && code === "10")
                ? `Stage ${code} — ${daysSince} day${daysSince !== 1 ? "s" : ""} since entering Stage 10`
                : `Stage ${code}`
            }
            placement="top"
          >
            <Box component="span" sx={{ whiteSpace: "nowrap" }}>
              {code}
              {daysSince !== null && code === "10" && (
                <Box
                  component="span"
                  sx={{ color: "text.secondary", ml: 0.5, fontSize: "0.75rem" }}
                >
                  [{daysSince}d]
                </Box>
              )}
            </Box>
          </Tooltip>
        );
      },
    },
    // ── Merged Plan + Elevation → "PL-EL" ────────────────────────────────
    {
      field: "planElevationNumber",
      headerName: "PL-EL",
      filterable: true,
      minWidth: 130,
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderHeader: () => (
        <Tooltip title="Plan – Elevation" placement="top">
          <Box
            sx={{
              fontWeight: 600,
              textTransform: "uppercase",
              fontSize: "0.875rem",
              color: "#374151",
              cursor: "default",
              userSelect: "none",
            }}
          >
            PL‑EL
          </Box>
        </Tooltip>
      ),
      valueGetter: (_params: any, row: any) => {
        const plan = row?.planDescription ?? "";
        const elev = row?.planElevationNumber ?? "";
        if (plan && elev) return `${plan} – ${elev}`;
        return plan || elev || null;
      },
      renderCell: (params) => (
        <Tooltip title={params.value ?? ""} placement="top">
          <Box
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
            }}
          >
            {params.value}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "listPrice",
      headerName: "Web/List Price",
      filterable: true,
      minWidth: 110,
      flex: 0.9,
      align: "center",
      headerAlign: "center",
      valueFormatter: (value: any) => {
        if (!value) return "";
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value);
      },
    },
    {
      field: "exitStrategy",
      headerName: "Exit %",
      filterable: true,
      minWidth: 70,
      flex: 0.6,
      align: "center",
      headerAlign: "center",
      valueGetter: (_params: any, row: any) => {
        if (row?.exitStrategy === null || row?.exitStrategy === undefined) return null;
        return row.exitStrategy;  // return number, not string
      },
      valueFormatter: (value: any) => (value != null ? `${value}%` : ""),
      renderCell: (params) => <div>{params.value}</div>,
    },
    {
      field: "bpPercent",
      headerName: "BP %",
      filterable: true,
      minWidth: 70,
      flex: 0.6,
      align: "center",
      headerAlign: "center",
      valueGetter: (_params: any, row: any) => {
        if (row?.bpPercent === null || row?.bpPercent === undefined) return null;
        return row.bpPercent;
      },
      valueFormatter: (value: any) => (value != null ? `${value}%` : ""),
      renderCell: (params) => <div>{params.value}</div>,
    },
    {
      field: "salesPriceAtBP",
      headerName: "BP Sales Price",
      filterable: true,
      minWidth: 130,
      flex: 0.9,
      align: "center",
      headerAlign: "center",
      valueFormatter: (value: any) => {
        if (!value) return "";
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value);
      },
    },
    {
      field: "blPercent",
      headerName: "BL %",
      filterable: true,
      minWidth: 70,
      flex: 0.6,
      align: "center",
      headerAlign: "center",
      valueGetter: (_params: any, row: any) => {
        if (row?.blPercent === null || row?.blPercent === undefined) return null;
        return row.blPercent;
      },
      valueFormatter: (value: any) => (value != null ? `${value}%` : ""),
      renderCell: (params) => <div>{params.value}</div>,
    },
    {
      field: "salesPriceAtBL",
      headerName: "BL Sales Price",
      filterable: true,
      minWidth: 130,
      flex: 0.9,
      align: "center",
      headerAlign: "center",
      valueFormatter: (value: any) => {
        if (!value) return "";
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value);
      },
    },
  ];

  // ── Filtered rows ─────────────────────────────────────────────────────────

  const filteredRows = React.useMemo(
    () => applyFilters<RowType>(rows, filt, columns),
    [rows, filt]
  );

  // ── Filter summary (shared between print and export) ──────────────────────

  const filterSummaryLines = React.useMemo(
    () => buildFilterSummary("Spec", stage, searchValue, selectedCommunities, filt, rows.length, filteredRows.length),
    [stage, searchValue, selectedCommunities, filt, rows.length, filteredRows.length]
  );

  // ── Print portal element ──────────────────────────────────────────────────

  const [printPortalEl, setPrintPortalEl] = React.useState<HTMLElement | null>(null);

  // in useEffect:
  React.useEffect(() => {
    const el = document.createElement("div");
    el.id = SPEC_PRINT_PORTAL_ID;
    document.body.appendChild(el);
    setPrintPortalEl(el);
    return () => { el.remove(); };
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Container maxWidth={false} disableGutters>
      <Card
        id="spec-list-print-root"
        sx={{ mt: 4, border: "1px solid #e5e7eb", borderRadius: 2 }}
      >
        <CardContent sx={{ overflow: "visible" }}>

          {/* ── Toolbar ── */}
          <ListPageToolbar<RowType>
            searchValue={searchValue}
            onSearchChange={handleSearchChange}
            selectedCommunities={selectedCommunities}
            onCommunityChange={handleCommunityChange}
            onCommunityDelete={handleCommunityDelete}
            communitySearch={communitySearch}
            onCommunitySearchChange={setCommunitySearch}
            onCommunityClose={() => setCommunitySearch("")}
            filteredSubdivisions={filteredSubdivisions}
            stage={stage}
            onStageChange={handleStageChange}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={handleClearFilters}
            paType="Spec"
            createHref="/spec/create"
            filteredRows={filteredRows}
            printColumns={PRINT_COLUMNS}
            filterSummaryLines={filterSummaryLines}
            legendItems={LEGEND_ITEMS}
          />

          {/* ── Print-only styles ── */}
          <style>{`
            @media print {
              @page { size: landscape; margin: 1cm; }
              body > *:not(#${SPEC_PRINT_PORTAL_ID}) { display: none !important; }
              #${SPEC_PRINT_PORTAL_ID} { display: block !important; }
            }
            #${SPEC_PRINT_PORTAL_ID} { display: none; }
          `}</style>

          {/* ── Mobile cards / Desktop DataGrid ── */}
          {isMobile ? (
            <Box sx={{ overflowY: "auto", maxHeight: "calc(100vh - 180px)", WebkitOverflowScrolling: "touch" }}>
              {loading ? (
                <Typography variant="body2" sx={{ color: "#6b7280", textAlign: "center", py: 4 }}>Loading…</Typography>
              ) : filteredRows.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 6 }}>
                  <Typography variant="body2" sx={{ color: "#9ca3af" }}>No rows to display</Typography>
                </Box>
              ) : (
                filteredRows.map((row) => (
                  <SpecMobileCard
                    key={row.jobId}
                    row={row}
                    onNavigate={(path, state) => navigation(path, { state })}
                  />
                ))
              )}
            </Box>
          ) : (
            <Box sx={{ height: "calc(100vh - 240px)", width: "100%" }}>
              <DataGrid
                disableColumnSelector
                disableDensitySelector
                hideFooter
                getRowId={(row) => row.jobId}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationChange}
                filterMode="server"
                onFilterModelChange={(newFilterModel) =>
                  handleFilterModelChange(newFilterModel as any)
                }
                disableRowSelectionOnClick
                columns={columns}
                rows={filteredRows}
                rowCount={filteredRows.length}
                slots={{ noRowsOverlay: NoRows }}
                sx={{
                  height: "100%",
                  "&": {
                    border: "none",
                    "& .MuiDataGrid-columnHeaders": {
                      borderBottom: "1px solid #e5e7eb",
                      "& .MuiDataGrid-columnHeaderTitle": {
                        fontWeight: 600,
                        textTransform: "uppercase",
                        fontSize: "0.875rem",
                        color: "#374151",
                      },
                    },
                    "& .MuiDataGrid-row": {
                      backgroundColor: "white",
                      borderBottom: "1px solid #e5e7eb",
                      "&:hover": { backgroundColor: "#f9fafb" },
                      "&:last-child": { borderBottom: "none" },
                    },
                    "& .MuiDataGrid-cell": {
                      border: "none",
                      padding: "1rem",
                      whiteSpace: "nowrap",
                      color: "#374151",
                    },
                    "& .MuiDataGrid-treeDataGroupingCellToggle": { alignSelf: "auto" },
                    "& .MuiDataGrid-iconSeparator": { display: "none" },
                    "& .MuiDataGrid-virtualScroller": { backgroundColor: "white" },
                    "& .MuiTablePagination-root": { color: "#6b7280" },
                    "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus-within": {
                      outline: "none",
                    },
                    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus": {
                      outline: "none",
                    },
                  },
                }}
                loading={loading}
              />
            </Box>
          )}
        </CardContent>


        {/* ── Hidden print table rendered into a body-level portal ── */}
        {printPortalEl && createPortal(
          <div style={{ padding: 16, fontFamily: "sans-serif" }}>
            {filterSummaryLines.map((line, i) => (
              <div
                key={i}
                style={{
                  fontSize: i === 0 ? 15 : 12,
                  fontWeight: i === 0 ? 700 : 400,
                  color: i === 0 ? "#111827" : "#6b7280",
                  marginBottom: i === 0 ? 4 : 2,
                }}
              >
                {line}
              </div>
            ))}
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 11, marginTop: 12 }}>
              <thead>
                <tr>
                  {PRINT_COLUMNS.map((c) => (
                    <th
                      key={c.field}
                      style={{
                        border: "1px solid #d1d5db",
                        padding: "4px 8px",
                        backgroundColor: "#f3f4f6",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        textAlign: "left",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row, i) => (
                  <tr key={row.jobId} style={{ backgroundColor: i % 2 === 0 ? "#fff" : "#f9fafb" }}>
                    {PRINT_COLUMNS.map((c) => (
                      <td
                        key={c.field}
                        style={{
                          border: "1px solid #e5e7eb",
                          padding: "3px 8px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {c.getValue(row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>,
          printPortalEl
        )}
      </Card>
    </Container>
  );
}
